/**
 * Store suggestion (THEME.md §7.21; D-120 AE2, T-191): the element of `sections/okt-store-suggestion.liquid`. It is
 * Horizon's dialog (it extends `DialogComponent`, so Escape, a click outside, the scroll lock and the closing animation
 * are Horizon's), shown as a modal or a drawer.
 *
 * - When: after the page has loaded and the browser is idle, it asks Shopify's browsing context suggestion
 *   (`/browsing_context_suggestions.json`, which Shopify's Geolocation app and Selecty called) for the visitor's
 *   country, this page's country and language excluded, as the Geolocation app did. Shopify's hreflang links (on every
 *   page but a 404) say which store serves that country: its own (`cs-CZ`: /cs-cz/…), else the root's (`en`: /…).
 *   The prompt shows only when that store's link isn't this page, so never on the visitor's own market's store.
 * - Never: for crawlers and automated browsers; for "Days before showing again" after the visitor stayed, or went
 *   with After Go to store "Hide for the set days"; on a store the visitor picked in Horizon's country or language
 *   selector (a localization form that leaves the page with another country or language), or went to with Go under
 *   "Remember the chosen store" (the default), within those days. Stored in localStorage, read and written in
 *   try/catch: `okt-store-suggestion` ({ at, action }) and `okt-store-choice` ({ at, country }).
 * - Language: the suggested store's when the browser lists it (`navigator.languages`), else this page's. In the
 *   suggested store's language, the texts are this section rendered at that store (the Section Rendering API through
 *   Horizon's section renderer) and the names come from the endpoint asked at that store; if either fails, the
 *   prompt keeps this page's language.
 * - Store line: the endpoint's names in the prompt's language (each part's `options`, asked with that language as
 *   Accept-Language, which the root follows): the country, the currency (code and symbol) and the language; the
 *   browser's own names (Intl.DisplayNames) where the endpoint gives none.
 * - Go: Shopify's localization form (`country_code`, `language_code`), posted to this store's route as Horizon's
 *   selector posts it; Shopify answers with this page in the new store.
 * - It waits while a cookie banner shows (Shopify's, or a theme banner marked `data-okt-consent-banner`), another
 *   dialog, the menu drawer or a mega menu panel is open, a product card's sizes show, a field or the country selector
 *   has focus, or the tab is hidden (every 2 s, for a minute at most; then on a later page).
 * - Its turn (THEME.md §7.24; T-218): once it has decided not to show on this page, or has been closed, the element
 *   takes `data-done` and dispatches `okt-store-suggestion:done`, so the newsletter pop-up can take its turn.
 * - Theme editor: only while the section is selected, with this store as the stand-in; nothing is stored.
 */
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { requestIdleCallback } from '@theme/utilities';

const SHOWN_KEY = 'okt-store-suggestion';
const CHOICE_KEY = 'okt-store-choice';
const DAY = 86400000;
const BOTS = /bot|crawl|spider|slurp|lighthouse|headless|facebookexternalhit|mediapartners/i;
const RETRY = 2000;
const RETRIES = 30;
const NAMES_QUERY = 'country[enabled]=true&currency[enabled]=true&language[enabled]=true';
const EDITABLE =
  'input:not([type="hidden"], [type="checkbox"], [type="radio"], [type="button"], [type="submit"]), textarea, select, [contenteditable]:not([contenteditable="false"])';

/** Sections selected in the theme editor, so a section rendered again after a setting change shows again */
const selectedSections = new Set();

/** @param {string} key */
function read(key) {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null');
  } catch {
    return null;
  }
}

/**
 * @param {string} key
 * @param {object} value
 */
function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage is blocked: the prompt can show again on a later page
  }
}

/**
 * @param {{ at?: number } | null} record
 * @param {number} days
 */
const isFresh = (record, days) => typeof record?.at === 'number' && Date.now() - record.at < days * DAY;

const isBot = () => navigator.webdriver === true || BOTS.test(navigator.userAgent);

/** The browser's languages as primary subtags: cs, de, en */
function browserLanguages() {
  const list = navigator.languages?.length ? navigator.languages : [navigator.language ?? ''];
  return list.map((language) => language.toLowerCase().split('-')[0]);
}

/**
 * This page in every store, from Shopify's hreflang links: a language and a country (cs-CZ: /cs-cz/…), or a language
 * alone for the root's markets (en: /…); x-default is left out.
 * @returns {{ language: string, country: string | null, url: URL }[]}
 */
function alternates() {
  const list = [];

  for (const link of document.head.querySelectorAll('link[rel="alternate"][hreflang]')) {
    const match = /^([a-z]{2,3})(?:-[a-z]{4})?(?:-([a-z]{2}))?$/i.exec(link.getAttribute('hreflang') ?? '');
    if (!match || !(link instanceof HTMLLinkElement)) continue;

    list.push({ language: match[1].toLowerCase(), country: match[2]?.toUpperCase() ?? null, url: new URL(link.href) });
  }

  return list;
}

/**
 * The store that serves a country: its own links, else the root's; with more than one language, one the browser
 * lists, else the first.
 * @param {string} country
 * @param {ReturnType<typeof alternates>} list
 */
function storeFor(country, list) {
  const own = list.filter((alternate) => alternate.country === country);
  const options = own.length ? own : list.filter((alternate) => !alternate.country);
  const languages = browserLanguages();

  return options.find((alternate) => languages.includes(alternate.language)) ?? options[0] ?? null;
}

/** @param {string} path */
const withSlash = (path) => (path.endsWith('/') ? path : `${path}/`);

/** @param {URL} url */
const isThisPage = (url) => withSlash(url.pathname) === withSlash(location.pathname);

/**
 * The root of the store a link belongs to (/cs-cz/), from this page's path below this store's root
 * @param {URL} url
 * @param {string} root - this store's root: / or /cs-cz/
 */
function storeRoot(url, root) {
  const page = withSlash(location.pathname).slice(root.length);
  const path = withSlash(url.pathname);

  return path.endsWith(page) ? path.slice(0, path.length - page.length) : null;
}

/**
 * Shopify's browsing context suggestion: the first suggestion's parts
 * @param {string} root - the store to ask; a store in a subfolder names things in its language
 * @param {string} query
 * @param {string | undefined} language - Accept-Language, which the root names things in
 * @param {AbortSignal} signal
 */
async function suggest(root, query, language, signal) {
  const response = await fetch(`${root}browsing_context_suggestions.json?${query}`, {
    signal,
    headers: language ? { 'Accept-Language': language } : {},
  });
  if (!response.ok) throw new Error(`browsing_context_suggestions ${response.status}`);

  const data = await response.json();
  return data?.suggestions?.[0]?.parts ?? null;
}

/**
 * The store line in the prompt's language: the endpoint's names (each part's options name every country, currency and
 * language it knows), else the browser's own
 * @param {any} parts - the endpoint's answer in the prompt's language
 * @param {{ country: string, currency?: string, language: string }} store
 * @param {string} language - the prompt's language
 */
function names(parts, store, language) {
  /** @param {'region' | 'language'} type @param {string} code */
  const display = (type, code) => {
    try {
      return new Intl.DisplayNames([language], { type }).of(code) ?? code;
    } catch {
      return code;
    }
  };

  return {
    country: parts?.country?.options?.[store.country] ?? display('region', store.country),
    currency: store.currency ? parts?.currency?.options?.[store.currency] ?? store.currency : '',
    language: parts?.language?.options?.[store.language] ?? display('language', store.language),
  };
}

/**
 * @param {number} ms
 * @param {AbortSignal} signal
 */
const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true }
    );
  });

/** The time of the page's load event */
const loaded = new Promise((resolve) => {
  if (document.readyState === 'complete') resolve(performance.now());
  else window.addEventListener('load', () => resolve(performance.now()), { once: true });
});

/**
 * Cookie banners and their preferences, while they show: Shopify's, and a theme banner's elements marked
 * `data-okt-consent-banner` (THEME.md §7.24, the overlays' order)
 */
const CONSENT = '#shopify-pc__banner, #shopify-pc__prefs, [data-okt-consent-banner]';

/** An open mega menu panel (Horizon keeps the closed ones inert) or the More menu (THEME.md §7.21, T-234) */
const MEGA_MENU = '.menu-list__submenu:not([inert]), header-menu[data-overflow-expanded="true"]';

/**
 * A product card's sizes while they show (Product cards › Size picker › On the card, THEME.md §7.6; T-228): opened by
 * the "+", hovered or focused
 */
function sizesShowing() {
  for (const band of document.querySelectorAll('.okt-quick-add-sizes')) {
    if (getComputedStyle(band).visibility === 'visible') return true;
  }
  return false;
}

/** @param {HTMLDialogElement} own */
function isBusy(own) {
  if (document.visibilityState !== 'visible') return true;
  if ([...document.querySelectorAll('dialog[open]')].some((dialog) => dialog !== own)) return true;
  if (document.querySelector('details[open][scroll-lock]')) return true;
  if (document.querySelector(MEGA_MENU) || sizesShowing()) return true;

  for (const element of document.querySelectorAll(CONSENT)) {
    if (element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden') return true;
  }

  const active = document.activeElement;
  return Boolean(active?.matches(EDITABLE) || active?.closest('localization-form-component'));
}

let watchingChoices = false;

/**
 * Remembers a store picked in Horizon's country or language selector: a localization form that leaves the page with
 * another country or language than the page's
 * @param {string} country
 * @param {string} language
 */
function watchChoices(country, language) {
  if (watchingChoices) return;
  watchingChoices = true;

  window.addEventListener('pagehide', () => {
    for (const form of document.querySelectorAll('localization-form-component form')) {
      if (!(form instanceof HTMLFormElement)) continue;

      const countryField = form.elements.namedItem('country_code');
      const languageField = form.elements.namedItem('language_code');
      const picked = countryField instanceof HTMLInputElement ? countryField.value : '';
      const spoken =
        languageField instanceof HTMLSelectElement || languageField instanceof HTMLInputElement ? languageField.value : '';

      if ((picked && picked !== country) || (spoken && spoken !== language)) {
        write(CHOICE_KEY, { at: Date.now(), country: picked || country });
        return;
      }
    }
  });
}

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {DialogComponent}
 */
class OktStoreSuggestion extends DialogComponent {
  /** @type {AbortController | undefined} */
  #abortController;

  /** Set when the visitor goes to the suggested store, so closing doesn't count as staying */
  #gone = false;

  get #designMode() {
    return this.hasAttribute('data-design-mode');
  }

  /** This store's root: / or /cs-cz/ */
  get #root() {
    return withSlash(this.dataset.root || '/');
  }

  get #content() {
    return this.querySelector('[data-okt-store-suggestion-content]');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#onClose, { signal });

    if (this.#designMode) {
      document.addEventListener('shopify:section:select', this.#onSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#onDeselect, { signal });
      if (selectedSections.has(this.dataset.sectionId)) this.showDialog();
      return;
    }

    if (!this.hasAttribute('data-enabled') || document.querySelector('okt-store-suggestion') !== this) return;

    window.addEventListener('pageshow', this.#onPageShow, { signal });
    watchChoices(this.dataset.country ?? '', this.dataset.language ?? '');
    this.#start(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @param {AbortSignal} signal */
  async #start(signal) {
    const days = Number(this.dataset.days) || 30;
    if (isBot() || isFresh(read(SHOWN_KEY), days)) return this.#done();

    try {
      const loadedAt = await loaded;
      await new Promise((resolve) => requestIdleCallback(resolve));
      if (signal.aborted || !(await this.#prepare(days, signal))) return this.#done();

      await wait(Math.max(0, loadedAt + Number(this.dataset.delay || 0) * 1000 - performance.now()), signal);

      for (let attempt = 0; isBusy(this.refs.dialog); attempt++) {
        if (attempt === RETRIES) return this.#done();
        await wait(RETRY, signal);
      }

      if (!isFresh(read(SHOWN_KEY), days)) this.showDialog();
      else this.#done();
    } catch {
      // The endpoint or the store didn't answer, or the page is leaving: no prompt on this page
      this.#done();
    }
  }

  /** Its turn is over on this page (THEME.md §7.24): the newsletter pop-up may take its own */
  #done() {
    if (this.hasAttribute('data-done')) return;
    this.setAttribute('data-done', '');
    this.dispatchEvent(new Event('okt-store-suggestion:done', { bubbles: true }));
  }

  /**
   * Decides whether to suggest a store, and fills the prompt in the language it shows in
   * @param {number} days
   * @param {AbortSignal} signal
   * @returns {Promise<boolean>}
   */
  async #prepare(days, signal) {
    const country = this.dataset.country ?? '';
    const language = this.dataset.language ?? '';
    const links = alternates();
    if (!country || !links.length) return false;

    // The visitor picked this store in the country selector, or went to it with Go (Remember the chosen store)
    const choice = read(CHOICE_KEY);
    if (isFresh(choice, days) && typeof choice.country === 'string') {
      const chosen = storeFor(choice.country, links);
      if (chosen && isThisPage(chosen.url)) return false;
    }

    const query = `country[enabled]=true&country[exclude]=${country}&currency[enabled]=true&language[enabled]=true&language[exclude]=${language}`;
    const parts = await suggest(this.#root, query, undefined, signal);
    const suggested = parts?.country?.handle;
    if (!suggested || suggested === country) return false;
    if (parts.country.options && !parts.country.options[suggested]) return false;

    const store = storeFor(suggested, links);
    if (!store || isThisPage(store.url)) return false;

    const target = { country: suggested, currency: parts.currency?.handle, language: store.language };

    // In the suggested store's language when the browser lists it: the texts rendered at that store, and its names
    const root = storeRoot(store.url, this.#root);
    if (root && store.language !== language && browserLanguages().includes(store.language)) {
      try {
        const [content, nameParts] = await Promise.all([
          this.#render(root, signal),
          suggest(root, NAMES_QUERY, store.language, signal),
        ]);

        if (content) {
          this.#content?.replaceWith(document.importNode(content, true));
          this.#fill(target, names(nameParts, target, store.language));
          return true;
        }
      } catch (error) {
        if (signal.aborted) throw error;
      }
    }

    const nameParts = await suggest(this.#root, NAMES_QUERY, language, signal).catch(() => null);
    this.#fill(target, names(nameParts, target, language));
    return true;
  }

  /**
   * This section rendered at another store, so in that store's language
   * @param {string} root
   * @param {AbortSignal} signal
   * @returns {Promise<Element | null>}
   */
  async #render(root, signal) {
    const { sectionRenderer } = await import('@theme/section-renderer');
    const url = new URL(root.replace(/\/$/, '') || '/', location.origin);
    const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId ?? '', false, url, signal);

    return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-okt-store-suggestion-content]');
  }

  /**
   * Writes the store line and points the form at the suggested store
   * @param {{ country: string, language: string }} target
   * @param {{ country: string, currency: string, language: string }} labels
   */
  #fill(target, labels) {
    const content = this.#content;
    if (!content) return;

    for (const [part, text] of Object.entries(labels)) {
      const element = content.querySelector(`[data-okt-store-${part}]`);
      if (element) element.textContent = text;
    }

    const form = content.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    // This store's route and this page, as Horizon's selector posts them (a rendering from another store holds that
    // store's route and the rendering's own address)
    form.action = `${this.#root}localization`;
    const returnField = form.elements.namedItem('return_to');
    const countryField = form.elements.namedItem('country_code');
    const languageField = form.elements.namedItem('language_code');
    if (returnField instanceof HTMLInputElement) returnField.value = `${location.pathname}${location.search}`;
    if (countryField instanceof HTMLInputElement) countryField.value = target.country;
    if (languageField instanceof HTMLInputElement) languageField.value = target.language;
  }

  /**
   * Go: Shopify's localization form takes the visitor to the store; in the theme editor it stays put. After Go to store
   * "Remember the chosen store" (the default) counts it as a pick in the country selector; "Hide for the set days"
   * keeps the prompt away, as Stay does.
   */
  go = (/** @type {SubmitEvent} */ event) => {
    if (this.#designMode) {
      event.preventDefault();
      return;
    }

    this.#gone = true;
    if (this.dataset.afterGo === 'hide') {
      write(SHOWN_KEY, { at: Date.now(), action: 'go' });
      return;
    }

    const country = this.#content?.querySelector('input[name="country_code"]');
    if (country instanceof HTMLInputElement) write(CHOICE_KEY, { at: Date.now(), country: country.value });
  };

  #onOpen = () => {
    const title = this.#content?.querySelector('[data-okt-store-suggestion-title]');
    if (title instanceof HTMLElement) title.focus({ preventScroll: true });
  };

  #onClose = () => {
    if (this.#designMode) return;
    if (!this.#gone) write(SHOWN_KEY, { at: Date.now(), action: 'stay' });
    this.#done();
  };

  /** Back from the store the visitor went to: the back-forward cache keeps the page as it was */
  #onPageShow = (/** @type {PageTransitionEvent} */ event) => {
    if (event.persisted && this.refs.dialog.open) this.closeDialog();
  };

  /** @param {Event} event */
  #isMine(event) {
    return event.target instanceof Element && event.target.contains(this);
  }

  #onSelect = (/** @type {Event} */ event) => {
    if (!this.#isMine(event)) return;
    selectedSections.add(this.dataset.sectionId);
    this.showDialog();
  };

  #onDeselect = (/** @type {Event} */ event) => {
    if (!this.#isMine(event)) return;
    selectedSections.delete(this.dataset.sectionId);
    this.closeDialog();
  };
}

if (!customElements.get('okt-store-suggestion')) customElements.define('okt-store-suggestion', OktStoreSuggestion);
